import { Group, Stack, Text, Paper } from "@mantine/core";
import { ISpecificationTableColumn } from "@mctypes/frontend.types";
import { Paths, PathValue } from "@mctypes/global.types";
import * as R from "ramda";
import React, { useEffect } from "react";

import { DeleteButton, VSpace } from "../../../../components";
import { geoReportValidation } from "../../../../components/ExcelImportValidator/ExcelImportValidator.utils";
import ExcelImportValidatorWithSpecTable from "../../../../components/ExcelImportValidator/ExcelImportValidatorWithSpecTable";
import InputUpload from "../../../../components/InputUpload/InputUpload";
import { IGeoTeknikkRapport } from "@mcapps/shared/reportSchemas/geoteknikSchema";
import BorringTable, { BORRING_TABLE_COLUMN } from "./BorrhulTable/BorrhulTable";
import GeoteknikGenrellInfo from "./GeoteknikGenerellInfo";
import GeoteknikMaps from "./GeoteknikMaps";
import GeoteknikSettings from "./GeoteknikSettings";
import { formatBorrhulDataFromExcel } from "./geoteknikUtils";

const GeoteknikRapport: React.FC<{
  data: IGeoTeknikkRapport;
  onUpdateEditData: (data: IGeoTeknikkRapport) => void;
  projectId: string;
  userName: string;
  createdAt?: Date;
}> = ({ data, onUpdateEditData, projectId, userName, createdAt }) => {
  const [editData, setEditData] = React.useState(R.clone(data));

  useEffect(() => {
    onUpdateEditData(data);
  }, []);

  React.useEffect(() => {
    setEditData(null);
    setTimeout(() => {
      setEditData(data);
    }, 50);
  }, [createdAt]);

  const setLabText = (text: string) => {
    updateData("laboratorieresultater", text);
  };

  const updateData = <P extends Paths<IGeoTeknikkRapport, 4>>(path: P, value: PathValue<IGeoTeknikkRapport, P>) => {
    setEditData((prevState) => {
      const updatedData = R.assocPath(path.split("."), value, prevState);
      return updatedData;
    });
    onUpdateEditData(R.assocPath(path.split("."), value, editData));
  };

  const BORRING_TABLE_COLUMN_SIMPLE: ISpecificationTableColumn[] = BORRING_TABLE_COLUMN.map(
    ({ field, headerName }) => ({ field, headerName }),
  );

  return (
    <Stack p="md" style={{ maxWidth: 800, margin: "0 auto" }}>
      {editData != null && (
        <>
          <GeoteknikSettings
            veiledningsTekst={editData.innstillinger?.veiledningsTekst}
            eksempelTekst={editData.innstillinger?.eksempelTekst}
            updateVeiledningsTekst={(bool) => updateData("innstillinger.veiledningsTekst", bool)}
            updateEksempelTekst={(bool) => updateData("innstillinger.eksempelTekst", bool)}
          />
          <VSpace />
          <GeoteknikGenrellInfo editData={editData} updateData={updateData} />
        </>
      )}

      <VSpace />

      <GeoteknikMaps
        geoteknikKoordinater={editData?.prosjektInformasjon?.koordinater}
        onUpdateGeoteknikKoordinater={(koordinater) => updateData("prosjektInformasjon.koordinater", koordinater)}
        sirkelDiameter={editData?.prosjektInformasjon?.sirkelDiameter}
        onUpdateSirkelDiameter={updateData}
        zoomLevel={editData?.prosjektInformasjon?.zoomLevel}
        onUpdateZoomLevel={(zoomLevel) => updateData("prosjektInformasjon.zoomLevel", zoomLevel)}
      />

      <VSpace />

      <Paper radius="sm" shadow="sm" p="sm" withBorder>
        <Stack gap="sm">
          <Text weight={700} size="lg">
            Laboratorieresultater
          </Text>
          <InputUpload setText={setLabText} text={editData?.laboratorieresultater} />
        </Stack>
      </Paper>

      <VSpace />

      <Paper radius="sm" shadow="sm" p="sm" withBorder>
        <Stack gap="sm">
          <Text weight={700} size="lg">
            Borehull
          </Text>
          <Text color="dimmed" size="sm">
            Excel fra GeoSuite
          </Text>

          <Group gap="sm" align="flex-start">
            <ExcelImportValidatorWithSpecTable
              validators={geoReportValidation}
              onValidated={(result) =>
                updateData("borrhuler", formatBorrhulDataFromExcel(result.sourceData as string[][]))
              }
              skipRows={1}
              specificationTable={{
                columns: BORRING_TABLE_COLUMN_SIMPLE,
                description: "Tabellen skal inneholde følgende kolonner:",
                data: [
                  {
                    id: "1",
                    x: "1",
                    y: "1",
                    z: "1",
                    metode: "1",
                    stopp: "1",
                    losm: "1",
                    fjell: "1",
                  },
                ],
              }}
            />
            <div style={{ flex: 1 }} />
            {editData?.borrhuler?.length > 0 && <DeleteButton outline onClick={() => updateData("borrhuler", null)} />}
          </Group>

          <VSpace />

          {editData?.borrhuler?.length > 0 && <BorringTable borrhuler={editData.borrhuler} />}
        </Stack>
      </Paper>

      <div style={{ height: 400 }} />
    </Stack>
  );
};

export default GeoteknikRapport;
